import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db, rtdb } from '../firebase/config';
import { doc, onSnapshot, collection, query, orderBy, addDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, onValue, set, onDisconnect } from 'firebase/database';
import { ArrowLeft, Send, Check, CheckCheck, MoreVertical, X, CornerDownRight, ShieldAlert } from 'lucide-react';
import { soundPlayer } from '../utils/audio';
import { sendBrowserNotification } from '../utils/notifications';
import { toast } from 'react-hot-toast';
import { getChatUnreadCount, markChatAsRead } from '../firebase/helpers';

const ChatPage = ({ navigate, chatId, initialPrompt = '' }) => {
    const { currentUser, userProfile, blockUser } = useAuth();
    const [chat, setChat] = useState(null);
    const [otherUserDoc, setOtherUserDoc] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState(initialPrompt ? initialPrompt + '\n' : '');
    const [replyingTo, setReplyingTo] = useState(null);
    const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const prevMsgCountRef = useRef(0);
    const inputRef = useRef(null);

    // Derived recipient ID: works for existing chats and brand new chats
    const otherUserId = chat?.participants?.find(p => p !== currentUser?.uid) || 
        (chatId && chatId.includes('_') ? chatId.split('_').find(id => id !== currentUser?.uid) : null);
    const otherUserProfile = chat?.participantProfiles?.[otherUserId] || otherUserDoc || {};

    // 1. Listen to chat metadata & other user profile
    useEffect(() => {
        if (!chatId || !currentUser) return;
        const chatRef = doc(db, 'chats', chatId);
        const unsubChat = onSnapshot(chatRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                setChat(data);
            }
        }, (err) => console.error("Chat sync error:", err));

        return () => unsubChat();
    }, [chatId, currentUser]);

    // Fetch other user doc if profiles aren't embedded in chat
    useEffect(() => {
        if (!otherUserId) return;
        const userRef = doc(db, 'users', otherUserId);
        const unsubUser = onSnapshot(userRef, (snap) => {
            if (snap.exists()) {
                setOtherUserDoc(snap.data());
            }
        });
        return () => unsubUser();
    }, [otherUserId]);

    // 2. Real-time messages sync with optimistic merge
    useEffect(() => {
        if (!chatId || !currentUser) return;

        const messagesQuery = query(
            collection(db, 'chats', chatId, 'messages'),
            orderBy('createdAt', 'asc')
        );

        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

        const unsubMessages = onSnapshot(messagesQuery, (snapshot) => {
            const serverMsgs = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(m => !m.createdAt?.toDate || m.createdAt.toDate() > twentyFourHoursAgo);

            // Merge server messages with optimistic ones that haven't shown up yet
            setMessages(prev => {
                const optimisticPending = prev.filter(p => p.optimistic && !serverMsgs.some(s => s.text === p.text && s.senderId === p.senderId));
                return [...serverMsgs, ...optimisticPending];
            });

            // Play chime and trigger notification ONLY IF tab is hidden (not actively viewing foreground chat)
            if (serverMsgs.length > prevMsgCountRef.current && prevMsgCountRef.current > 0) {
                const latest = serverMsgs[serverMsgs.length - 1];
                const soundPref = localStorage.getItem('batcheet_sound_enabled') !== 'false';
                if (latest.senderId !== currentUser?.uid && document.hidden) {
                    if (soundPref) {
                        soundPlayer.playNotificationSound();
                    }
                    sendBrowserNotification(`Message from ${otherUserProfile.name || 'User'}`, {
                        body: latest.text || 'Shared a message'
                    });
                }
            }
            prevMsgCountRef.current = serverMsgs.length;
            setLoading(false);
        }, (err) => {
            console.error("Messages sync error:", err);
            setLoading(false);
        });

        return () => unsubMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, currentUser?.uid, otherUserProfile.name]);

    // 3. Mark incoming unread messages as read & reset unread count in real-time
    useEffect(() => {
        if (!chatId || !currentUser) return;
        
        // Safely reset unread count for current user in chat document
        markChatAsRead(chatId, currentUser.uid);

        // Mark individual incoming messages as read in subcollection
        const unreadIncoming = messages.filter(m => m.senderId !== currentUser.uid && !m.isRead && !m.optimistic);
        if (unreadIncoming.length > 0) {
            unreadIncoming.forEach(m => {
                updateDoc(doc(db, 'chats', chatId, 'messages', m.id), { isRead: true }).catch(() => {});
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, currentUser, messages.length, chat?.unreadCount?.[currentUser?.uid]]);



    // 4. Presence: Other user online/offline status
    useEffect(() => {
        if (!rtdb || !otherUserId) return;
        const userStatusRef = ref(rtdb, `/status/${otherUserId}`);
        const unsubPresence = onValue(userStatusRef, (snap) => {
            setIsOnline(snap.val()?.state === 'online');
        });
        return () => unsubPresence();
    }, [otherUserId]);

    // 5. Instant Realtime Database Typing Indicator Listener
    useEffect(() => {
        if (!rtdb || !chatId || !otherUserId) return;
        const typingRef = ref(rtdb, `/typing/${chatId}/${otherUserId}`);
        const unsubTyping = onValue(typingRef, (snap) => {
            setIsOtherUserTyping(!!snap.val());
        });
        return () => unsubTyping();
    }, [chatId, otherUserId]);

    // 6. Auto scroll to bottom smoothly
    const scrollToBottom = (instant = false) => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
        }
    };

    useEffect(() => {
        scrollToBottom(false);
    }, [messages, isOtherUserTyping]);

    // 7. Typing broadcast via RTDB
    const handleInputChange = (e) => {
        setInputText(e.target.value);
        if (!chatId || !currentUser || !rtdb) return;

        const myTypingRef = ref(rtdb, `/typing/${chatId}/${currentUser.uid}`);
        set(myTypingRef, true).catch(() => {});
        onDisconnect(myTypingRef).set(false).catch(() => {});

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            set(myTypingRef, false).catch(() => {});
        }, 2000);
    };

    // 6. Send Message with Zero Delay (Instant Optimistic Update)
    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        const text = inputText.trim();
        if (!text || !currentUser || !chatId) return;

        const activeReply = replyingTo;

        // Instant optimistic update (0ms UI latency)
        const tempId = 'temp_' + Date.now();
        const optimisticMsg = {
            id: tempId,
            senderId: currentUser.uid,
            text,
            replyTo: activeReply ? {
                id: activeReply.id,
                text: activeReply.text,
                senderName: activeReply.senderId === currentUser.uid ? 'You' : otherUserProfile.name || 'User'
            } : null,
            createdAt: new Date(),
            isRead: false,
            optimistic: true
        };

        setMessages(prev => [...prev, optimisticMsg]);
        setInputText('');
        setReplyingTo(null);

        // Immediate scroll to bottom
        setTimeout(() => scrollToBottom(true), 10);

        try {
            // Stop typing status immediately
            if (rtdb && chatId && currentUser) {
                set(ref(rtdb, `/typing/${chatId}/${currentUser.uid}`), false).catch(() => {});
            }
            const chatRef = doc(db, 'chats', chatId);
            setDoc(chatRef, {
                [`typingStatus.${currentUser.uid}`]: false
            }, { merge: true }).catch(() => {});

            // Add to messages collection
            const messagesRef = collection(db, 'chats', chatId, 'messages');
            await addDoc(messagesRef, {
                senderId: currentUser.uid,
                text,
                replyTo: optimisticMsg.replyTo,
                createdAt: serverTimestamp(),
                isRead: false
            });

            // Update chat metadata with last message and increment other user's unreadCount
            const chatUpdate = {
                lastMessage: {
                    text,
                    createdAt: serverTimestamp(),
                    senderId: currentUser.uid
                },
                lastUpdated: serverTimestamp()
            };

            if (otherUserId) {
                chatUpdate.participants = [currentUser.uid, otherUserId];
                chatUpdate.participantProfiles = {
                    [currentUser.uid]: {
                        name: userProfile?.displayName || currentUser.displayName || 'User',
                        photoURL: userProfile?.photoURL || currentUser.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${currentUser.uid}`,
                        username: userProfile?.username || 'user'
                    },
                    [otherUserId]: {
                        name: otherUserProfile.name || otherUserDoc?.displayName || 'User',
                        photoURL: otherUserProfile.photoURL || otherUserDoc?.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${otherUserId}`,
                        username: otherUserProfile.username || otherUserDoc?.username || 'user'
                    }
                };
                const prevOther = getChatUnreadCount(chat, otherUserId);
                chatUpdate.unreadCount = {
                    ...(chat?.unreadCount || {}),
                    [otherUserId]: prevOther + 1,
                    [currentUser.uid]: 0
                };
            }

            await setDoc(chatRef, chatUpdate, { merge: true });

        } catch (err) {
            console.error("Send message error:", err);
            toast.error("Message could not be synced. Check connection.");
        }
    };

    // Keyboard shortcut: Enter to send, Shift+Enter for new line
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSelectReply = (msg) => {
        const senderName = msg.senderId === currentUser?.uid ? 'You' : (otherUserProfile.name || 'User');
        setReplyingTo({
            id: msg.id,
            text: msg.text,
            senderName,
            senderId: msg.senderId
        });
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleBlock = async () => {
        if (!otherUserId) return;
        const confirm = window.confirm(`Are you sure you want to block ${otherUserProfile.name || 'this user'}?`);
        if (!confirm) return;
        try {
            await blockUser(otherUserId);
            toast.success("User blocked.");
            navigate('chats');
        } catch (err) {
            toast.error("Could not block user.");
        }
    };

    return (
        <div className="flex flex-col h-full w-full bg-white dark:bg-[#111827]/95 backdrop-blur-md border-x-0 sm:border border-slate-200/90 dark:border-slate-800/80 rounded-none sm:rounded-3xl shadow-xs overflow-hidden animate-fade-in">
            {/* Chat Top Header - Clean, No Audio/Video call buttons */}
            <div className="p-3 sm:p-3.5 px-3.5 sm:px-4 bg-slate-50/80 dark:bg-slate-900/60 border-b border-slate-200/80 dark:border-slate-800/80 flex items-center justify-between z-10 select-none">
                <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                    <button
                        onClick={() => {
                            markChatAsRead(chatId, currentUser?.uid);
                            navigate('chats');
                        }}
                        className="p-1.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
                        title="Back to messages"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>

                    {/* Other User Info */}
                    <div
                        onClick={() => otherUserId && navigate('profile', { userId: otherUserId })}
                        className="flex items-center gap-2.5 cursor-pointer group min-w-0"
                    >
                        <div className="relative flex-shrink-0">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full p-[1.5px] bg-gradient-to-tr from-sky-400 to-indigo-500 shadow-xs">
                                <img
                                    src={otherUserProfile.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${otherUserId}`}
                                    alt=""
                                    className="w-full h-full rounded-full object-cover bg-slate-100 dark:bg-slate-800"
                                />
                            </div>
                            {isOnline && (
                                <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-[#111827] rounded-full shadow-xs"></span>
                            )}
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-slate-100 group-hover:text-sky-500 dark:group-hover:text-sky-400 transition-colors truncate">
                                {otherUserProfile.name || 'User'}
                            </h3>
                            <p className="text-[10px] sm:text-[11px] flex items-center gap-1 font-medium">
                                {isOnline ? (
                                    <span className="text-emerald-500 dark:text-emerald-400 font-semibold">● Online</span>
                                ) : (
                                    <span className="text-slate-400 dark:text-slate-500">Offline</span>
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* More Menu */}
                <div className="relative">
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        className="p-1.5 sm:p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>

                    {menuOpen && (
                        <div className="absolute right-0 top-10 w-36 bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl py-1 z-30 animate-slide-up text-xs font-semibold">
                            <button
                                onClick={handleBlock}
                                className="w-full text-left px-3.5 py-2 text-rose-500 hover:bg-slate-50 dark:hover:bg-slate-800/80 flex items-center gap-2"
                            >
                                <ShieldAlert className="w-4 h-4" />
                                <span>Block User</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Messages Stream */}
            <div className="flex-1 overflow-y-auto p-3.5 sm:p-4 space-y-3 no-scrollbar bg-slate-50/40 dark:bg-slate-950/20">
                {loading ? (
                    <div className="py-16 text-center space-y-2">
                        <div className="w-7 h-7 mx-auto rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                        <p className="text-xs text-slate-400">Loading conversation...</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="py-20 text-center space-y-2">
                        <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">No messages in the last 24 hours.</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500">Say hello and start the instant conversation! 👋</p>
                    </div>
                ) : (
                    messages.map((msg) => {
                        const isMe = msg.senderId === currentUser?.uid;
                        const timeStr = msg.createdAt?.toDate 
                            ? msg.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
                            : msg.createdAt instanceof Date 
                            ? msg.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : 'now';
                        const isShort = !msg.replyTo && !msg.text.includes('\n') && msg.text.length < 32;

                        return (
                            <div
                                key={msg.id}
                                className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} group animate-fade-in`}
                            >
                                <div
                                    onDoubleClick={() => handleSelectReply(msg)}
                                    title="Double-tap to quote reply"
                                    className={`relative max-w-[85%] sm:max-w-[72%] w-fit px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-2xl shadow-xs transition-all ${
                                        isMe
                                            ? 'bg-gradient-to-tr from-sky-500 to-indigo-600 text-white rounded-br-xs'
                                            : 'bg-white dark:bg-[#1e293b] text-slate-900 dark:text-slate-100 border border-slate-200/80 dark:border-slate-700/80 rounded-bl-xs'
                                    }`}
                                >
                                    {/* Quoted Reply Banner */}
                                    {msg.replyTo && (
                                        <div className={`mb-1.5 px-2.5 py-1 rounded-lg text-xs border-l-2 ${
                                            isMe
                                                ? 'bg-black/20 text-white/90 border-white/80'
                                                : 'bg-slate-100 dark:bg-slate-800/90 text-slate-700 dark:text-slate-200 border-sky-500'
                                        }`}>
                                            <p className={`font-bold text-[10px] truncate ${isMe ? 'text-sky-200' : 'text-sky-600 dark:text-sky-400'}`}>
                                                {msg.replyTo.senderName}
                                            </p>
                                            <p className="truncate text-[11px] opacity-85 leading-tight mt-0.5">
                                                {msg.replyTo.text}
                                            </p>
                                        </div>
                                    )}

                                    {/* Snug Inline Layout for Short Messages vs Block for Multiline */}
                                    {isShort ? (
                                        <div className="flex items-baseline gap-2.5 justify-between">
                                            <span className="text-xs sm:text-sm font-normal break-words">{msg.text}</span>
                                            <span className={`inline-flex items-center gap-1 text-[10px] flex-shrink-0 select-none ${isMe ? 'text-white/80' : 'text-slate-400 dark:text-slate-500'}`}>
                                                <span>{timeStr}</span>
                                                {isMe && (
                                                    msg.optimistic ? (
                                                        <span className="text-[9px] opacity-75">●</span>
                                                    ) : msg.isRead ? (
                                                        <CheckCheck className="w-3.5 h-3.5 text-sky-300" />
                                                    ) : (
                                                        <Check className="w-3.5 h-3.5 text-white/50" />
                                                    )
                                                )}
                                            </span>
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap font-normal break-words">
                                                {msg.text}
                                            </p>
                                            <div className={`flex items-center justify-end gap-1 mt-0.5 text-[10px] select-none ${isMe ? 'text-white/80' : 'text-slate-400 dark:text-slate-500'}`}>
                                                <span>{timeStr}</span>
                                                {isMe && (
                                                    msg.optimistic ? (
                                                        <span className="text-[9px] opacity-75">●</span>
                                                    ) : msg.isRead ? (
                                                        <CheckCheck className="w-3.5 h-3.5 text-sky-300" />
                                                    ) : (
                                                        <Check className="w-3.5 h-3.5 text-white/50" />
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Desktop-only hover reply icon */}
                                <button
                                    onClick={() => handleSelectReply(msg)}
                                    className="hidden sm:group-hover:flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-sky-500 dark:hover:text-sky-400 transition-opacity mt-0.5 px-1 py-0.5 cursor-pointer"
                                    title="Reply"
                                >
                                    <CornerDownRight className="w-3 h-3" />
                                    <span>Reply</span>
                                </button>
                            </div>
                        );
                    })
                )}

                {/* Live Typing Indicator */}
                {isOtherUserTyping && (
                    <div className="flex items-center gap-2 py-1.5 px-3 bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700/80 rounded-full w-fit shadow-xs animate-fade-in">
                        <div className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-bounce"></span>
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-bounce [animation-delay:150ms]"></span>
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-bounce [animation-delay:300ms]"></span>
                        </div>
                        <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">typing...</span>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Quoted Message Preview Bar - Slim & Non-bulky */}
            {replyingTo && (
                <div className="px-3 sm:px-4 py-1.5 bg-slate-50 dark:bg-slate-900 border-t border-slate-200/80 dark:border-slate-800/80 flex items-center justify-between text-xs animate-slide-up">
                    <div className="flex items-center gap-2 min-w-0">
                        <CornerDownRight className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
                        <div className="min-w-0">
                            <span className="font-bold text-sky-600 dark:text-sky-400 text-[10px] block">
                                Replying to {replyingTo.senderId === currentUser?.uid ? 'yourself' : otherUserProfile.name || 'User'}
                            </span>
                            <p className="text-slate-500 dark:text-slate-400 truncate text-[11px] leading-tight">{replyingTo.text}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setReplyingTo(null)}
                        className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg transition-colors"
                        title="Cancel reply"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}

            {/* Bottom Message Input Bar - Ultra-sleek Pill Design */}
            <form onSubmit={handleSendMessage} className="p-2 sm:p-2.5 bg-white dark:bg-[#0b0f17] border-t border-slate-200/80 dark:border-slate-800/80 flex items-center gap-2">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Message..."
                    className="flex-1 px-4 py-2 sm:py-2.5 bg-slate-100 dark:bg-slate-800/90 rounded-full text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 border-none transition-all"
                />
                <button
                    type="submit"
                    disabled={!inputText.trim()}
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-tr from-sky-500 to-indigo-600 text-white shadow-sm shadow-sky-500/25 hover:scale-105 active:scale-95 disabled:opacity-30 disabled:hover:scale-100 disabled:cursor-not-allowed transition-all flex items-center justify-center flex-shrink-0"
                    title="Send message"
                >
                    <Send className="w-4 h-4 ml-0.5" />
                </button>
            </form>
        </div>
    );
};

export default ChatPage;
